/**
 * SchoolSearchField.js
 *
 * A search field for the unauthenticated "find schools" page. Adapted from the
 * onboarding SearchFieldSites: it searches CommunityShare sites via the public
 * site endpoint and falls back to Google Places for addresses. Unlike the
 * onboarding version it does not use location bias (there is no prior zip-code
 * step) and it confirms a selection directly rather than going through a
 * separate picking step.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { MagnifyingGlassIcon, XMarkIcon, BuildingOffice2Icon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/24/solid';
import useIsMobile from '@/hooks/useIsMobile';
import titleCase from '@/utils/titleCase';
import usePublicSites from '../hooks/usePublicSites';

SchoolSearchField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  hideDetails: PropTypes.bool,
};

/**
 * An autocomplete search field for CommunityShare sites and Google Places.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered search field.
 */
export default function SchoolSearchField(props) {
  const {
    label,
    value = '',
    onSelect,
    onInputChange,
    hideDetails = false,
  } = props;

  const { t } = useTranslation();

  const uid = useId();
  const inputId = `school-search-${uid}`;
  const listboxId = `school-search-listbox-${uid}`;

  const [inputValue, setInputValue] = useState(value);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [focused, setFocused] = useState(false);

  const places = useMapsLibrary('places');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const isTouch = useIsMobile();

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Debounce the query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Show/hide dropdown based on query.
  useEffect(() => {
    if (debouncedQuery.length > 0) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setPlaceSuggestions([]);
    }
  }, [debouncedQuery]);

  // Google Places Autocomplete (no location bias on this public page).
  useEffect(() => {
    if (!places || !debouncedQuery || debouncedQuery.length <= 1 || !hasInteracted) {
      setPlaceSuggestions([]);
      return;
    }

    let cancelled = false;

    const fetchSuggestions = async () => {
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: debouncedQuery,
        });
        if (!cancelled) {
          setPlaceSuggestions(suggestions || []);
        }
      } catch (e) {
        console.error('Places autocomplete error', e);
        if (!cancelled) setPlaceSuggestions([]);
      }
    };

    fetchSuggestions();
    return () => { cancelled = true; };
  }, [places, debouncedQuery, hasInteracted]);

  // CommunityShare site database search via the public endpoint.
  const { sites: siteResults, isFetching } = usePublicSites(debouncedQuery, {
    enabled: hasInteracted,
  });

  // Close dropdown on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setFocused(false);
      inputRef.current?.blur();
    }
  }

  /**
   * Handles selection of a CommunityShare site from the database.
   *
   * @param {object} site - The selected public site.
   */
  function onSelectSite(site) {
    const siteName = site.name || '';
    setInputValue(siteName);
    setShowDropdown(false);
    setFocused(false);
    onSelect({
      siteId: site.id,
      name: siteName,
      addressLine1: site.addressLine1 || '',
      addressLine2: site.addressLine2 || '',
      city: site.city || '',
      state: site.state || '',
      zipCode: site.zipCode || '',
      isActive: site.isActive ?? false,
      type: 'Site',
    });
  }

  /**
   * Handles selection of an address from Google Places. Not a known
   * CommunityShare site, so it is treated as a potential (not-found) site.
   *
   * @param {object} suggestion - The selected Google Place suggestion.
   */
  async function onSelectPlace(suggestion) {
    const placePrediction = suggestion.placePrediction;
    const place = placePrediction.toPlace();

    try {
      await place.fetchFields({
        fields: ['id', 'displayName', 'addressComponents', 'formattedAddress'],
      });
    } catch (e) {
      console.error('fetchFields error', e);
    }

    const addressComponents = place.addressComponents || [];
    const getComponent = (types) => {
      const c = addressComponents.find((comp) => types.every((type) => comp.types.includes(type)));
      return c ? c.longText : '';
    };
    const getShortComponent = (types) => {
      const c = addressComponents.find((comp) => types.every((type) => comp.types.includes(type)));
      return c ? c.shortText : '';
    };

    const streetNumber = getComponent(['street_number']);
    const routeLong = getComponent(['route']);
    const city = getComponent(['locality']) || getComponent(['sublocality_level_1']);
    const state = getShortComponent(['administrative_area_level_1']);
    const zipCode = getComponent(['postal_code']);

    const displayText = suggestion.placePrediction?.text?.toString() || place.formattedAddress || '';
    setInputValue(displayText);
    setShowDropdown(false);
    setFocused(false);

    onSelect({
      siteId: place.id || `google_place_${Date.now()}`,
      name: displayText,
      addressLine1: `${streetNumber} ${routeLong}`.trim(),
      addressLine2: '',
      city,
      state,
      zipCode,
      type: 'PotentialSite',
    });
  }

  /**
   * Confirms the free-text address the user typed as a potential site.
   */
  function onSelectCustomAddress() {
    const val = inputValue.trim();
    setShowDropdown(false);
    setFocused(false);
    inputRef.current?.blur();
    onSelect({
      siteId: `custom_${Date.now()}`,
      name: val,
      type: 'PotentialSite',
    });
  }

  function handleClear() {
    setInputValue('');
    setHasInteracted(true);
    if (onInputChange) onInputChange('');
  }

  const hasSites = debouncedQuery.length > 1 && siteResults.length > 0;
  const hasPlaces = placeSuggestions.length > 0;
  const loading = isFetching && debouncedQuery.length > 1;

  const showUseThisAddress = inputValue.trim().length >= 4 && !hasPlaces && !hasSites && !loading;
  const showNoMatch = debouncedQuery.trim().length >= 5 && !hasPlaces && !hasSites && !loading;

  const heightClass = isTouch ? 'min-h-12' : 'min-h-0';
  const itemHoverClass = !isTouch ? 'hover:bg-cs-orange-50 active:bg-cs-orange-100' : 'active:bg-cs-orange-100';
  const iconColourClass = !isTouch ? 'text-cs-gray-400 group-hover:text-cs-orange-400' : 'text-cs-gray-400';
  const labelColourClass = !isTouch ? 'text-cs-gray-900 group-hover:text-cs-orange-600' : 'text-cs-gray-900';
  const subLabelColourClass = !isTouch ? 'text-cs-gray-500 group-hover:text-cs-orange-400' : 'text-cs-gray-500';

  return (
    <div ref={containerRef} className="flex flex-col items-start relative w-full">
      <label htmlFor={inputId} className="block text-sm font-medium text-cs-gray-700 mb-1">
        {label || t('find_schools.search_label')}
      </label>
      <div
        className={classNames(
          'w-full bg-white border rounded-md px-4 py-1 flex items-center gap-2 transition-colors',
          focused ? 'border-cs-blue-500' : 'border-cs-gray-300',
          heightClass
        )}
      >
        <MagnifyingGlassIcon className="size-5 text-cs-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          id={inputId}
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          type="text"
          value={inputValue}
          placeholder={t('find_schools.search_placeholder')}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            setHasInteracted(true);
            if (onInputChange) onInputChange(val);
          }}
          onFocus={() => {
            setFocused(true);
            if (!hideDetails && debouncedQuery.length > 1) setShowDropdown(true);
          }}
          className="border-none flex-1 min-w-0 bg-transparent font-normal text-base leading-[24px] text-cs-gray-900 placeholder:text-cs-gray-400 focus:outline-none focus:ring-0 focus:border-transparent truncate"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="flex-shrink-0 text-cs-gray-400 hover:text-cs-gray-900 focus:outline-none"
          >
            <XMarkIcon className="size-5" />
          </button>
        )}
      </div>

      {!hideDetails && showDropdown && (hasSites || hasPlaces || loading || showNoMatch || showUseThisAddress) && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Search results"
          className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-cs-gray-200 rounded-xl shadow-md overflow-hidden"
        >
          <div className="overflow-y-auto max-h-[280px]">
            {loading && (
              <p className="px-4 pt-4 text-sm text-cs-gray-500 pb-4">{t('find_schools.searching')}</p>
            )}

            {!loading && (
              <>
                <div className="px-4 pt-3 pb-2">
                  {hasSites || hasPlaces ? (
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {t('find_schools.possible_matches')}
                    </span>
                  ) : null}
                </div>
                {hasSites && siteResults.map((site) => (
                  <div
                    key={site.id}
                    role="option"
                    aria-selected="false"
                    tabIndex={-1}
                    className={classNames(
                      'group px-4 py-3 cursor-pointer flex items-start gap-3',
                      itemHoverClass
                    )}
                    onClick={() => onSelectSite(site)}
                  >
                    <BuildingOffice2Icon className={classNames('size-5 flex-shrink-0 mt-0.5', iconColourClass)} />
                    <div className="flex flex-col min-w-0">
                      <span className={classNames('text-sm underline leading-snug', labelColourClass)}>
                        {site.name}
                      </span>
                      <span className={classNames('text-xs leading-snug mt-0.5', subLabelColourClass)}>
                        {titleCase(site.addressLine1 || '')}
                        {site.city && `, ${titleCase(site.city)}`}
                        {site.state && `, ${site.state}`}
                      </span>
                    </div>
                  </div>
                ))}

                {hasSites && hasPlaces && (
                  <div className="border-t border-gray-100 mx-4 my-1" />
                )}

                {hasPlaces && placeSuggestions.slice(0, 3).map((suggestion, idx) => {
                  const text = suggestion.placePrediction?.text?.toString() || '';
                  return (
                    <div
                      key={idx}
                      role="option"
                      aria-selected="false"
                      tabIndex={-1}
                      className={classNames(
                        'group px-4 py-3 cursor-pointer flex items-start gap-3',
                        itemHoverClass
                      )}
                      onClick={() => onSelectPlace(suggestion)}
                    >
                      <MapPinIcon className={classNames('size-5 flex-shrink-0 mt-0.5', iconColourClass)} />
                      <div className="flex flex-col min-w-0">
                        <span className={classNames('text-sm underline leading-snug', labelColourClass)}>
                          {text}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {showNoMatch && (
                  <div className="px-4 py-4 text-center">
                    <p className="text-sm text-cs-gray-500">{t('find_schools.no_matches')}</p>
                    <p className="text-xs text-cs-gray-400 mt-1">{t('find_schools.no_matches_hint')}</p>
                  </div>
                )}

                {showUseThisAddress && (
                  <div
                    role="option"
                    aria-selected="false"
                    tabIndex={-1}
                    className={classNames(
                      'px-4 py-4 cursor-pointer flex items-center gap-3 border-t border-cs-gray-100 group',
                      !isTouch ? 'hover:bg-cs-orange-50' : '',
                      'active:bg-cs-orange-100'
                    )}
                    onClick={onSelectCustomAddress}
                  >
                    <PlusCircleIcon className="size-5 flex-shrink-0 text-cs-orange-500 group-hover:text-cs-orange-600" />
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-cs-gray-700 group-hover:text-cs-orange-600">
                        {t('find_schools.use_this_address')}
                      </span>
                      <span className="text-sm text-cs-gray-500 group-hover:text-cs-orange-500">
                        {inputValue}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
